import React, { useEffect, useRef, useCallback, useState } from 'react';
import packageJson from '../../package.json';
import PreQualFunnelLayout from './PreQualFunnelLayout';
import { pushToDataLayer } from '../utils/gtm';
import { withExpVariant } from '../utils/kamExpVariant';
import { buildLendAPIIframeSrc, getValidSessionFromURL } from '../utils/lendAPIUtils';
import {
  enhancePayloadWithStoredData,
  applyStoredDataFallback,
  storeLendAPIPrequalData,
  mapLendAPIToPrequalSchema,
  processLendAPITrackingEvents,
  callLendAPIWebhook,
  LendAPIPayload,
  LendAPIPostMessageEvent,
  LendAPITrackingResponse,
  LendAPITrackingData,
} from '../utils/lendAPIPrequalHelper';
import {
  LendApiPrequalTab,
  LENDAPI_PREQUAL_TRACKING_EVENTS,
  LEND_API_PRODUCT_ID_QA,
  LEND_API_PRODUCT_ID_PROD,
  isProduction,
  LEND_API_ORIGIN,
} from '../constants';
import {
  mapLendAPIToIterableStarted,
  mapLendAPIToIterableCompleted,
} from '../utils/lendAPIIterableHelper';
import { sendCustomEventToIterable } from '../utils/iterable';
import { recordFinanceIdentity } from '../utils/recordFinanceIdentity';
import LendAPIIframe from '../shared/components/LendAPIIframe';

const DYNAMIC_APP_INTERNAL_VERSION =
  process.env.REACT_APP_INTERNAL_VERSION || packageJson.version || '1.0.0';

interface LendAPIPrequalProps {
  productId: string;
}

// Module-level helper: Maps current tab to GA pageview data for NEXT tab
const getNextTabPageview = (
  currentTab: string
): { event: string; detailed_page_name: string; site_section: string; funnel: string } | null => {
  switch (currentTab) {
    case LendApiPrequalTab.LETS_GET_STARTED:
      return {
        event: 'pageview',
        detailed_page_name: 'monthly payment',
        site_section: 'trident finance',
        funnel: 'lendapi pre-qual',
      };

    case LendApiPrequalTab.FINANCING_DETAIL:
      return {
        event: 'pageview',
        detailed_page_name: 'down payment',
        site_section: 'trident finance',
        funnel: 'lendapi pre-qual',
      };

    case LendApiPrequalTab.DOWN_PAYMENT:
      return {
        event: 'pageview',
        detailed_page_name: 'about you',
        site_section: 'trident finance',
        funnel: 'lendapi pre-qual',
      };

    case LendApiPrequalTab.ABOUT_YOU:
      return {
        event: 'pageview',
        detailed_page_name: 'fico score',
        site_section: 'trident finance',
        funnel: 'lendapi pre-qual',
      };

    case LendApiPrequalTab.SELECT_FICO:
      return {
        event: 'pageview',
        detailed_page_name: 'results',
        site_section: 'trident finance',
        funnel: 'lendapi pre-qual',
      };

    default:
      return null;
  }
};

// Module-level helper: Maps current tab to Kameleoon goal for NEXT tab
const getNextTabKameleoonGoal = (currentTab: string): string | null => {
  switch (currentTab) {
    case LendApiPrequalTab.LETS_GET_STARTED:
      return 'custom-lendapi-financing-detail';

    case LendApiPrequalTab.FINANCING_DETAIL:
      return 'custom-lendapi-down-payment';

    case LendApiPrequalTab.DOWN_PAYMENT:
      return 'custom-lendapi-about-you';

    case LendApiPrequalTab.ABOUT_YOU:
      return 'custom-lendapi-fico-score';

    default:
      return null;
  }
};

// Module-level helper: Handles Google Analytics tracking for tab progression
const handleGATracking = (tabName: string): void => {
  const nextTabPageview = getNextTabPageview(tabName);
  if (nextTabPageview) {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push(withExpVariant(nextTabPageview));
    console.log('[LendAPI][TRACKING] GA pageview event sent:', nextTabPageview.detailed_page_name);
  }
};

// Module-level helper: Handles Kameleoon goal tracking for tab progression
const handleKameleoonTracking = (tabName: string): void => {
  const nextTabKameleoonGoal = getNextTabKameleoonGoal(tabName);
  if (nextTabKameleoonGoal && (window as any).kameleoonQueue) {
    (window as any).kameleoonQueue.push(['Goals.processConversion', nextTabKameleoonGoal]);
    console.log('[LendAPI][TRACKING] Kameleoon custom goal sent:', nextTabKameleoonGoal);
  }
};

const LendAPIPrequal: React.FC<LendAPIPrequalProps> = ({ productId }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const storedEmailRef = useRef<string | null>(null);
  const storedFirstNameRef = useRef<string | null>(null);
  const storedLastNameRef = useRef<string | null>(null);
  const storedCustomRef = useRef<string | null>(null);
  const [currentTab, setCurrentTab] = useState<LendApiPrequalTab | string>(
    LendApiPrequalTab.LETS_GET_STARTED
  );
  const [lastKnownTab, setLastKnownTab] = useState<LendApiPrequalTab>(
    LendApiPrequalTab.LETS_GET_STARTED
  );

  // AboutYou tab handler - stores user data and sends PreQualStarted event
  const handleAboutYouTab = useCallback(async (payload: LendAPIPayload) => {
    // Map the iframe payload to our prequal schema format
    const mappedData = mapLendAPIToPrequalSchema(payload);

    // Store user data from AboutYou tab for later use in SelectFICO (needed for declined applications)
    if (mappedData.email) {
      storedEmailRef.current = mappedData.email;
    }
    if (mappedData.firstName) {
      storedFirstNameRef.current = mappedData.firstName;
    }
    if (mappedData.lastName) {
      storedLastNameRef.current = mappedData.lastName;
    }
    if (mappedData.customParams) {
      storedCustomRef.current = mappedData.customParams;
    }

    // Store in localStorage with appropriate key
    const { imtId } = mappedData;
    storeLendAPIPrequalData(mappedData, imtId);

    // Send PreQualStarted event after AboutYou completion
    try {
      const startedEvent = mapLendAPIToIterableStarted(payload, mappedData);
      await sendCustomEventToIterable(startedEvent);
      console.log('[LendAPI] PreQualStarted event sent successfully');
    } catch (error) {
      console.error('[LendAPI] Error sending PreQualStarted event:', error);
    }
  }, []);

  // SelectFICO tab handler - sends PreQualCompleted event with tracking logic
  const handleSelectFicoTab = useCallback(async (payload: LendAPIPayload) => {
    const storedData = {
      email: storedEmailRef.current,
      firstName: storedFirstNameRef.current,
      lastName: storedLastNameRef.current,
      customParams: storedCustomRef.current,
    };

    const enhancedPayload = enhancePayloadWithStoredData(payload, storedData);
    const mappedData = mapLendAPIToPrequalSchema(enhancedPayload);
    const finalMappedData = applyStoredDataFallback(mappedData, storedData);

    try {
      const applicationId = finalMappedData.prequalApplicationId;
      if (applicationId) {
        console.log('[LendAPI] Triggering webhook processing for applicationId:', applicationId);

        callLendAPIWebhook(
          applicationId,
          isProduction(),
          LEND_API_PRODUCT_ID_QA,
          LEND_API_PRODUCT_ID_PROD
        )
          .then(() => {
            recordFinanceIdentity('lead');
          })
          .catch((webhookError) => {
            console.error('[LendAPI] Webhook call failed:', webhookError);
          });

        console.log('[LendAPI] Webhook processing triggered');
      } else {
        console.warn('[LendAPI] No applicationId found, skipping webhook trigger');
      }
    } catch (webhookError) {
      console.error('[LendAPI] Error in webhook trigger block:', webhookError);
    }

    try {
      const completedEvent = mapLendAPIToIterableCompleted(enhancedPayload, finalMappedData);

      const finalEventData = {
        ...completedEvent,
        isLendAPI: true,
        applicationId: finalMappedData.prequalApplicationId,
      };

      const response: LendAPITrackingResponse = await sendCustomEventToIterable(finalEventData);

      const trackingData: LendAPITrackingData = {
        response,
        finalMappedData,
        completedEvent,
      };

      processLendAPITrackingEvents(trackingData, pushToDataLayer, LENDAPI_PREQUAL_TRACKING_EVENTS);
    } catch (error) {
      console.error('[LendAPI] Error sending PreQualCompleted event:', error);
    }
  }, []);

  // Initialize tracking events when component mounts
  // Sends GA pageview and Kameleoon goal for the initial LetsGetStarted tab
  useEffect(() => {
    try {
      pushToDataLayer(
        LENDAPI_PREQUAL_TRACKING_EVENTS.GA_EVENTS.PAGEVIEW,
        withExpVariant({
          detailed_page_name: 'purchase timeframe',
          site_section: 'trident finance',
          funnel: 'lendapi pre-qual',
        })
      );
      console.log('[LendAPI][TRACKING] GA pageview event sent: purchase timeframe');

      if (window.kameleoonQueue) {
        window.kameleoonQueue.push([
          'Goals.processConversion',
          LENDAPI_PREQUAL_TRACKING_EVENTS.KAMELEOON_GOALS.LETS_GET_STARTED,
        ]);
        console.log(
          `[LendAPI][TRACKING] Kameleoon custom goal sent: ${LENDAPI_PREQUAL_TRACKING_EVENTS.KAMELEOON_GOALS.LETS_GET_STARTED}`
        );
      }
    } catch (error) {
      console.error('[LendAPI][TRACKING] Error sending initial pageview or Kameleoon goal:', error);
    }
  }, []);

  // Main postMessage handler for LendAPI iframe communication
  // Processes form submissions and manages cross-tab data flow and tracking events
  useEffect(() => {
    const handlePostMessage = async (event: LendAPIPostMessageEvent) => {
      // Only process messages from LendAPI domain
      if (event.origin !== LEND_API_ORIGIN) return;

      try {
        if (event.data?.type !== 'dimensionChange') {
          console.log(
            `[LendAPI][handleMessage] | Received event of type ${event.data?.type} for tab ${event.data?.payload?.tab_name}`,
            JSON.stringify(event.data)
          );
        }

        // Handle formSubmission events
        if (event.data && event.data.type === 'formSubmission' && event.data.payload) {
          const tabName = event.data.payload.tab_name;

          if (Object.values(LendApiPrequalTab).includes(tabName as LendApiPrequalTab)) {
            const validTab = tabName as LendApiPrequalTab;
            setCurrentTab(validTab);
            setLastKnownTab(validTab);
          } else {
            setCurrentTab(tabName);
          }

          // Send GA and Kameleoon tracking for next tab
          handleGATracking(tabName);
          handleKameleoonTracking(tabName);

          // Log unknown tabs (except SelectFICO which has no next tab)
          const hasNextTab =
            getNextTabPageview(tabName) !== null || getNextTabKameleoonGoal(tabName) !== null;
          if (!hasNextTab && tabName !== LendApiPrequalTab.SELECT_FICO) {
            console.log('[LendAPI][TRACKING] Unknown tab_name for GA tracking:', tabName);
          }

          // Route to appropriate tab handler
          if (tabName === LendApiPrequalTab.ABOUT_YOU) {
            await handleAboutYouTab(event.data.payload as LendAPIPayload);
          } else if (tabName === LendApiPrequalTab.SELECT_FICO) {
            await handleSelectFicoTab(event.data.payload as LendAPIPayload);
          }
        }

        // Handle pageLoad event with tab_name
        if (
          event.data &&
          event.data.type === 'pageLoad' &&
          event.data.payload &&
          event.data.payload.tab_name
        ) {
          const { payload } = event.data;
          const tabName = payload.tab_name;

          if (Object.values(LendApiPrequalTab).includes(tabName as LendApiPrequalTab)) {
            const validTab = tabName as LendApiPrequalTab;
            setCurrentTab(validTab);
            setLastKnownTab(validTab);
          } else {
            setCurrentTab(tabName);
          }

          window.scrollTo(0, 0);
        }
      } catch (error) {
        console.error('[LendAPI] Error handling postMessage:', error);
      }
    };

    window.addEventListener('message', handlePostMessage);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('message', handlePostMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // VERSION IDENTIFIER - to confirm latest code is loaded
  if ((window as any).__lendApiPrequalVersionLogged !== true) {
    console.log(`Dynamic App Version: ${DYNAMIC_APP_INTERNAL_VERSION}`);
    (window as any).__lendApiPrequalVersionLogged = true;
  }

  const sessionId = getValidSessionFromURL();
  const iframeSrc = buildLendAPIIframeSrc(productId, sessionId);

  return (
    <PreQualFunnelLayout currentTab={currentTab} lastKnownTab={lastKnownTab} showProgressBar>
      <LendAPIIframe src={iframeSrc} iframeRef={iframeRef} currentTab={currentTab} />
    </PreQualFunnelLayout>
  );
};

export default React.memo(LendAPIPrequal);
